/**
 * PumpAssignmentOverlay - Full-screen overlay for managing pump-to-chemical assignments.
 *
 * Features:
 * - Shows pumps grouped by number (derived dynamically from existing assignments)
 * - When pumpCount is known, shows fixed pump slots 1..pumpCount
 * - "Current" assignment highlighted per pump
 * - Add new assignment (with chemical autocomplete from dosing_chemicals)
 * - Edit existing assignment (dates, chemical, notes)
 * - Delete assignment
 * - Overlap errors shown inline (from 409 API response)
 */
import { useState, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Paper,
  Badge,
  ActionIcon,
  Textarea,
  TextInput,
  Alert,
  Loader,
  Center,
  Tooltip,
  Autocomplete,
  NumberInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import {
  usePumpAssignments,
  useCreatePumpAssignment,
  useUpdatePumpAssignment,
  useDeletePumpAssignment,
} from '@/hooks/usePumpAssignments';
import { useDosingChemicals } from '@/hooks/useDosingEvents';
import type { PumpAssignment } from '@/types/pumpAssignment';

interface PumpAssignmentOverlayProps {
  opened: boolean;
  onClose: () => void;
  deviceId: string;
  deviceName: string;
  pumpCount?: number | null;
}

interface EditingAssignment {
  id?: number; // undefined = creating new
  pump_number: number;
  chemical_name: string;
  label: string;
  start_at: string | null;
  end_at: string | null;
  notes: string;
}

const EMPTY_EDIT: EditingAssignment = {
  pump_number: 1,
  chemical_name: '',
  label: '',
  start_at: null,
  end_at: null,
  notes: '',
};

export function PumpAssignmentOverlay({
  opened,
  onClose,
  deviceId,
  deviceName,
  pumpCount,
}: PumpAssignmentOverlayProps) {
  const { data, isLoading, error } = usePumpAssignments(opened ? deviceId : undefined);
  const { data: chemicalsData } = useDosingChemicals();
  const createMutation = useCreatePumpAssignment(deviceId);
  const updateMutation = useUpdatePumpAssignment(deviceId);
  const deleteMutation = useDeletePumpAssignment(deviceId);

  const [editing, setEditing] = useState<EditingAssignment | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Build autocomplete data from dosing_chemicals
  const chemicalOptions = (chemicalsData?.chemicals || []).map((c) => c.name);

  const assignments = data?.assignments || [];

  const [newPumpNumber, setNewPumpNumber] = useState<number | ''>('');

  // Build pump groups: merge fixed pump slots (from pumpCount) with existing assignments
  const pumpGroups = useMemo(() => {
    const pumpNumbers = new Set(assignments.map((a) => a.pump_number));
    // When pumpCount is known, ensure all fixed slots 1..pumpCount are present
    if (pumpCount != null && pumpCount > 0) {
      for (let i = 1; i <= pumpCount; i++) {
        pumpNumbers.add(i);
      }
    }
    return Array.from(pumpNumbers)
      .sort((a, b) => a - b)
      .map((pumpNum) => {
        const pumpAssignments = assignments
          .filter((a) => a.pump_number === pumpNum)
          .sort((a, b) => new Date(b.start_at).getTime() - new Date(a.start_at).getTime());
        const current = pumpAssignments.find((a) => a.end_at === null);
        return { pumpNumber: pumpNum, assignments: pumpAssignments, current };
      });
  }, [assignments, pumpCount]);

  const handleStartEdit = (assignment: PumpAssignment) => {
    setEditing({
      id: assignment.id,
      pump_number: assignment.pump_number,
      chemical_name: assignment.chemical_name,
      label: assignment.label || '',
      start_at: assignment.start_at,
      end_at: assignment.end_at,
      notes: assignment.notes || '',
    });
    setFormError(null);
  };

  const handleStartCreate = (pumpNumber: number) => {
    setEditing({
      ...EMPTY_EDIT,
      pump_number: pumpNumber,
      start_at: new Date().toISOString(),
    });
    setFormError(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.chemical_name.trim()) {
      setFormError('Chemical name is required');
      return;
    }
    if (!editing.start_at) {
      setFormError('Start date/time is required');
      return;
    }

    setFormError(null);

    try {
      if (editing.id) {
        // Update
        await updateMutation.mutateAsync({
          assignmentId: editing.id,
          data: {
            chemical_name: editing.chemical_name.trim(),
            label: editing.label.trim() || undefined,
            start_at: editing.start_at,
            end_at: editing.end_at ?? undefined,
            notes: editing.notes.trim() || undefined,
          },
        });
      } else {
        // Create
        await createMutation.mutateAsync({
          pump_number: editing.pump_number,
          chemical_name: editing.chemical_name.trim(),
          label: editing.label.trim() || undefined,
          start_at: editing.start_at,
          end_at: editing.end_at ?? undefined,
          notes: editing.notes.trim() || undefined,
        });
      }
      setEditing(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleDelete = async (assignmentId: number) => {
    if (!confirm('Delete this pump assignment? Dosing events will show the fallback pump label.')) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(assignmentId);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const formatDateTime = (iso: string) => {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Whether the device has a known fixed pump count
  const hasFixedPumps = pumpCount != null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Pump Assignments — ${deviceName}`}
      fullScreen
      transitionProps={{ transition: 'slide-up' }}
    >
      <Stack gap="lg" maw={800} mx="auto">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error instanceof Error ? error.message : 'Failed to load assignments'}
          </Alert>
        )}

        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            {/* Editing Form */}
            {editing && (
              <Paper withBorder p="md" bg="blue.0">
                <Stack gap="sm">
                  <Text fw={600}>
                    {editing.id ? `Edit Assignment — Pump ${editing.pump_number}` : `New Assignment — Pump ${editing.pump_number}`}
                  </Text>

                  {formError && (
                    <Alert icon={<IconAlertCircle size={16} />} color="red" withCloseButton onClose={() => setFormError(null)}>
                      {formError}
                    </Alert>
                  )}

                  <Autocomplete
                    label="Chemical"
                    placeholder="Type or select a chemical"
                    data={chemicalOptions}
                    value={editing.chemical_name}
                    onChange={(val) => setEditing({ ...editing, chemical_name: val })}
                    maxDropdownHeight={200}
                  />

                  <TextInput
                    label="Pump Label (optional)"
                    placeholder="e.g., Alkalinity, Calcium, Magnesium"
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.currentTarget.value })}
                    maxLength={100}
                  />

                  <Group grow>
                    <DateTimePicker
                      label="Start"
                      placeholder="Pick start date & time"
                      value={editing.start_at}
                      onChange={(val) => setEditing({ ...editing, start_at: val })}
                      clearable={false}
                    />
                    <DateTimePicker
                      label="End (leave empty for ongoing)"
                      placeholder="Pick end date & time"
                      value={editing.end_at}
                      onChange={(val) => setEditing({ ...editing, end_at: val })}
                      clearable
                    />
                  </Group>

                  <Textarea
                    label="Notes (optional)"
                    placeholder="e.g., Doses at :05 past hour"
                    value={editing.notes}
                    onChange={(e) => setEditing({ ...editing, notes: e.currentTarget.value })}
                    maxLength={500}
                    autosize
                    minRows={1}
                    maxRows={3}
                  />

                  <Group justify="flex-end" gap="xs">
                    <Button variant="subtle" onClick={() => setEditing(null)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      loading={createMutation.isPending || updateMutation.isPending}
                      leftSection={<IconCheck size={16} />}
                    >
                      {editing.id ? 'Save Changes' : 'Add Assignment'}
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            )}

            {/* Pump Groups */}
            {pumpGroups.map(({ pumpNumber, assignments: pumpAssignments, current }) => (
              <Paper key={pumpNumber} withBorder p="md">
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text fw={600}>
                        Pump {pumpNumber}{current?.label ? ` — ${current.label}` : ''}
                      </Text>
                      {current ? (
                        <Badge color="blue" variant="light">
                          {current.chemical_name}
                        </Badge>
                      ) : (
                        <Badge color="gray" variant="light">
                          Unassigned
                        </Badge>
                      )}
                    </Group>
                    <Tooltip label="Add assignment">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() => handleStartCreate(pumpNumber)}
                        disabled={!!editing}
                      >
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>

                  {pumpAssignments.length === 0 ? (
                    <Text size="sm" c="dimmed">
                      No assignments — dosing events will show &quot;Pump {pumpNumber}&quot;
                    </Text>
                  ) : (
                    pumpAssignments.map((assignment) => (
                      <Paper
                        key={assignment.id}
                        withBorder
                        p="xs"
                        bg={assignment.end_at === null ? undefined : 'gray.0'}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Stack gap={2}>
                            <Group gap="xs">
                              <Text size="sm" fw={500}>
                                {assignment.chemical_name}
                              </Text>
                              {assignment.end_at === null && (
                                <Badge size="xs" color="green">
                                  Current
                                </Badge>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {formatDateTime(assignment.start_at)}
                              {' → '}
                              {assignment.end_at
                                ? formatDateTime(assignment.end_at)
                                : 'Ongoing'}
                            </Text>
                            {assignment.notes && (
                              <Text size="xs" c="dimmed" fs="italic">
                                {assignment.notes}
                              </Text>
                            )}
                          </Stack>
                          <Group gap={4}>
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() => handleStartEdit(assignment)}
                                disabled={!!editing}
                              >
                                <IconPencil size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => handleDelete(assignment.id)}
                                disabled={!!editing}
                                loading={deleteMutation.isPending}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Paper>
                    ))
                  )}
                </Stack>
              </Paper>
            ))}

            {/* Add assignment for a new pump number — hidden when pump count is known */}
            {!hasFixedPumps && (
              <Paper withBorder p="md" style={{ borderStyle: 'dashed' }}>
                <Group gap="sm">
                  <Text size="sm" c="dimmed">Add assignment for pump #</Text>
                  <NumberInput
                    value={newPumpNumber}
                    onChange={(val) => setNewPumpNumber(typeof val === 'number' ? val : '')}
                    min={1}
                    max={9999}
                    placeholder="#"
                    w={80}
                    size="xs"
                  />
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    disabled={!newPumpNumber || !!editing || pumpGroups.some(g => g.pumpNumber === newPumpNumber)}
                    onClick={() => {
                      if (typeof newPumpNumber === 'number') {
                        handleStartCreate(newPumpNumber);
                        setNewPumpNumber('');
                      }
                    }}
                  >
                    Add
                  </Button>
                </Group>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
